import React, { useRef, useState } from 'react';
import { Box, TextField, Typography, Button, InputAdornment, Alert } from '@mui/material';
import {
  Badge as BadgeIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Language as WebIcon,
  FormatQuote as MottoIcon,
  Gavel as LicenceIcon,
  HealthAndSafety as NhisIcon,
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Autorenew as ReplaceIcon,
} from '@mui/icons-material';

import { BRAND, brandFieldSx, brandGhostButtonSx } from '../../../brand';
import StepHeading from '../StepHeading';
import { FormSection, FormRow, Field } from '../FormLayout';

// ============================================================================
//  Step 2 — Contact & Identity
// ============================================================================
//  Contacts, registration codes and the facility logo. The logo is not
//  decoration: it is printed on patient ID cards, receipts and lab reports,
//  so it is read here as a data URL, resized client-side, and stored inline
//  on the facility row — the shape the existing card/report templates expect.

const MAX_LOGO_BYTES = 2 * 1024 * 1024;   // 2 MB before resize
const LOGO_EDGE      = 400;               // px — square-fit bound after resize

// Target for the stored data URL. The logo is not just posted once at setup:
// it lives inline on the `assembly` row and is returned by /companyInfo on
// every visit to the sign-in screen, so an oversized one is a permanent tax on
// page load — and, before the body-parser limit was fixed, it was what pushed
// requests past the cap. ~180 KB of base64 is plenty for a 400px mark.
const LOGO_TARGET_CHARS = 180 * 1024;

// Draw the image into a canvas at `edge` and encode it.
function encode(img, edge, mime, quality) {
  const scale = Math.min(1, edge / Math.max(img.width, img.height));
  const w = Math.max(1, Math.round(img.width * scale));
  const h = Math.max(1, Math.round(img.height * scale));

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');

  // JPEG has no alpha channel: without an explicit white fill, transparent
  // areas encode as black. Logos are almost always transparent-on-white, so
  // paint the background before drawing.
  if (mime === 'image/jpeg') {
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, w, h);
  }

  ctx.drawImage(img, 0, 0, w, h);
  return canvas.toDataURL(mime, quality);
}

// Downscale in a canvas rather than pulling in another dependency.
//
// PNG is tried first because it preserves transparency, which matters when the
// mark is placed on the navy sign-in panel. Photographic or highly detailed
// logos compress badly as PNG, so if the result is still over budget we fall
// back through progressively cheaper encodings rather than storing something
// enormous or rejecting an image the operator legitimately wants to use.
function resizeToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read that file'));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('That file is not a readable image'));
      img.onload = () => {
        const attempts = [
          [LOGO_EDGE, 'image/png',  undefined],
          [LOGO_EDGE, 'image/jpeg', 0.9],
          [320,       'image/jpeg', 0.85],
          [256,       'image/jpeg', 0.8],
        ];

        let out = '';
        for (const [edge, mime, quality] of attempts) {
          out = encode(img, edge, mime, quality);
          if (out.length <= LOGO_TARGET_CHARS) break;
        }

        // Even the smallest attempt can exceed the target for a pathological
        // input. Accept it — it is still far under the server's limit — rather
        // than failing on an image that will work fine.
        resolve(out);
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

function IdentityStep({ data, errors, onChange }) {
  const set = (field) => (e) => onChange(field, e.target.value);
  const fileRef = useRef(null);
  const [logoError, setLogoError] = useState('');
  const [busy, setBusy] = useState(false);

  // The wizard flagged a missing logo on Continue, and the operator has not
  // picked one since. Drives the whole card's error styling.
  const missing = !!errors.logo && !data.logo;

  const adorn = (Icon) => ({
    startAdornment: (
      <InputAdornment position="start">
        <Icon sx={{ fontSize: 19, color: BRAND.navy }} />
      </InputAdornment>
    ),
  });

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    // Reset immediately so picking the same file twice still fires onChange.
    e.target.value = '';
    if (!file) return;

    setLogoError('');

    if (!file.type.startsWith('image/')) {
      setLogoError('Please choose an image file (PNG or JPG).');
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      setLogoError('That image is larger than 2 MB. Please choose a smaller file.');
      return;
    }

    try {
      setBusy(true);
      onChange('logo', await resizeToDataUrl(file));
    } catch (err) {
      setLogoError(err.message || 'Could not process that image.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box>
      <StepHeading
        icon={BadgeIcon}
        title="Contact & Identity"
        subtitle="How patients, insurers and this system reach your facility — and the logo that appears on ID cards, receipts and reports."
      />

      {/* ── Contact ─────────────────────────────────────────────────────── */}
      <FormSection
        first
        title="Contact details"
        caption="Used for system notices, printed documents and correspondence with insurers."
      >
        <FormRow>
          <Field span={6}>
            <TextField
              fullWidth required type="email"
              label="Facility Email"
              placeholder="info@yourhospital.com"
              value={data.email}
              onChange={set('email')}
              error={!!errors.email}
              helperText={errors.email || 'Official address for claims and system notices'}
              sx={brandFieldSx}
              InputProps={adorn(EmailIcon)}
            />
          </Field>

          <Field span={6}>
            <TextField
              fullWidth required
              label="Facility Phone"
              placeholder="0244000000"
              value={data.phone}
              onChange={set('phone')}
              error={!!errors.phone}
              helperText={errors.phone || 'Main switchboard or front-desk line'}
              sx={brandFieldSx}
              InputProps={adorn(PhoneIcon)}
            />
          </Field>

          <Field span={6}>
            <TextField
              fullWidth
              label="Website"
              placeholder="www.yourhospital.com"
              value={data.website}
              onChange={set('website')}
              error={!!errors.website}
              helperText={errors.website || 'Optional — printed on patient ID cards'}
              sx={brandFieldSx}
              InputProps={adorn(WebIcon)}
            />
          </Field>

          <Field span={6}>
            <TextField
              fullWidth
              label="Motto / Strapline"
              placeholder="e.g. Compassionate care, always"
              value={data.motto}
              onChange={set('motto')}
              error={!!errors.motto}
              helperText={errors.motto || 'Optional — shown under the facility name on documents'}
              sx={brandFieldSx}
              InputProps={adorn(MottoIcon)}
            />
          </Field>
        </FormRow>
      </FormSection>

      {/* ── Registration ────────────────────────────────────────────────── */}
      <FormSection
        title="Registration & accreditation"
        caption="Identifiers issued by the regulator and the NHIA. Both can be added later, but NHIS claims cannot be submitted without the facility code."
      >
        <FormRow>
          <Field span={6}>
            <TextField
              fullWidth
              label="Facility Licence Number"
              placeholder="e.g. HEFRA/2024/00123"
              value={data.licenseNo}
              onChange={set('licenseNo')}
              error={!!errors.licenseNo}
              helperText={errors.licenseNo || 'Optional'}
              sx={brandFieldSx}
              InputProps={adorn(LicenceIcon)}
            />
          </Field>

          <Field span={6}>
            <TextField
              fullWidth
              label="NHIS Facility Code"
              placeholder="e.g. 0123456"
              value={data.nhisCode}
              onChange={set('nhisCode')}
              error={!!errors.nhisCode}
              helperText={errors.nhisCode || 'Optional — required before submitting NHIS claims'}
              sx={brandFieldSx}
              InputProps={adorn(NhisIcon)}
            />
          </Field>
        </FormRow>
      </FormSection>

      {/* ── Logo ──────────────────────────────────────────────────────────
           Required, not optional. The logo is printed on patient ID cards,
           receipts and lab reports — a facility that finishes setup without
           one ends up issuing unbranded documents, and there is no prompt
           later that would catch it. `missing` is the validation error raised
           by the wizard on Continue; `logoError` is a local file-picker
           problem (wrong type, too large). */}
      <FormSection
        title="Facility logo *"
        caption="Appears on patient ID cards, receipts, lab reports and the sign-in screen. Required."
      >
        <Box
          sx={{
            display: 'flex', alignItems: 'center', gap: { xs: 2, sm: 3 }, flexWrap: 'wrap',
            p: { xs: 2, sm: 3 }, borderRadius: '14px',
            border: `1.5px dashed ${
              data.logo ? BRAND.green : missing ? BRAND.danger : BRAND.line
            }`,
            background: data.logo
              ? BRAND.greenSoft
              : missing ? '#FDECEC' : BRAND.canvas,
            transition: 'all .2s ease',
          }}
        >
          <Box
            sx={{
              width: { xs: 84, sm: 104 }, height: { xs: 84, sm: 104 },
              borderRadius: '12px', flexShrink: 0,
              background: '#fff', border: `1px solid ${missing ? BRAND.danger : BRAND.line}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              overflow: 'hidden', p: 1,
            }}
          >
            {data.logo ? (
              <Box component="img" src={data.logo} alt="Facility logo preview"
                sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
            ) : (
              <BadgeIcon sx={{ fontSize: 36, color: missing ? BRAND.danger : BRAND.inkFaint }} />
            )}
          </Box>

          <Box sx={{ flex: 1, minWidth: { xs: 0, sm: 260 } }}>
            <Typography
              sx={{
                fontSize: 14, fontWeight: 700, mb: .5,
                color: missing ? BRAND.danger : BRAND.ink,
              }}
            >
              {data.logo
                ? 'Logo ready'
                : missing ? 'A facility logo is required' : 'Upload your facility logo'}
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: BRAND.inkMuted, lineHeight: 1.7, mb: 2 }}>
              PNG or JPG, up to 2&nbsp;MB. A square image on a transparent or white
              background reproduces best on ID cards. Large files are resized
              automatically.
            </Typography>

            <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
              <Button
                variant={missing ? 'contained' : 'outlined'}
                size="small" disabled={busy}
                startIcon={data.logo ? <ReplaceIcon /> : <UploadIcon />}
                onClick={() => fileRef.current?.click()}
                sx={missing
                  ? {
                      textTransform: 'none', fontWeight: 700, borderRadius: '10px',
                      background: BRAND.danger, '&:hover': { background: BRAND.danger, filter: 'brightness(.94)' },
                    }
                  : brandGhostButtonSx}
              >
                {busy ? 'Processing…' : data.logo ? 'Replace' : 'Choose image'}
              </Button>
              {data.logo && (
                <Button
                  variant="text" size="small" startIcon={<DeleteIcon />}
                  onClick={() => { onChange('logo', ''); setLogoError(''); }}
                  sx={{ textTransform: 'none', fontWeight: 600, color: BRAND.danger }}
                >
                  Remove
                </Button>
              )}
            </Box>

            <input
              ref={fileRef} type="file" accept="image/png,image/jpeg"
              onChange={handleFile} style={{ display: 'none' }}
            />
          </Box>
        </Box>

        {(logoError || missing) && (
          <Alert severity="error" sx={{ mt: 1.75, borderRadius: '10px' }}>
            {logoError || errors.logo}
          </Alert>
        )}
      </FormSection>
    </Box>
  );
}

export default IdentityStep;
